import { Portal } from '@mui/material';
import { createContext, FunctionComponent, PropsWithChildren, useContext, useState } from 'react';
import { gamePlayStore, useGameplaySelector } from '../../store/GameplayStore';

type FaceOffPortalContextType = {
	portalContainer: HTMLElement | undefined;
	setPortalContainer: (container: HTMLElement | undefined) => void;
};

const FaceOffPortalContext = createContext<FaceOffPortalContextType | undefined>(undefined);

/**
 * Provides a reference to a Portal that can be used to render the Question and answers with the correct
 * layering in Face Off
 */
export const FaceOffPortalProvider: FunctionComponent<PropsWithChildren> = (props) => {
	const [portalContainer, setPortalContainer] = useState<HTMLElement | undefined>(undefined);

	return <FaceOffPortalContext.Provider value={{ portalContainer, setPortalContainer }}>
		{props.children}
	</FaceOffPortalContext.Provider>;
};

export function useFaceOffPortal(): FaceOffPortalContextType {
	const context = useContext(FaceOffPortalContext);
	if (context) {
		return context;
	} else {
		throw new Error('useFaceOffPortal must be used within FaceOffPortalProvider');
	}
}

export const FaceOffPortal: FunctionComponent<PropsWithChildren> = (props) => {
	const phase = useGameplaySelector(gamePlayStore.selectors.phase);
	// Arguably this could match the 'head-to-head' condition used elsewhere and include playOrPass, but this
	// introduces a nice effect in playOrPass with the question defocused.
	const isFaceOff = phase === 'faceoff';

	const { portalContainer } = useFaceOffPortal();
	const renderQuestionInFaceOffPortal = isFaceOff && (portalContainer !== undefined);

	return renderQuestionInFaceOffPortal
		? <Portal container={portalContainer}>
			{props.children}
		</Portal>
		: props.children;
};